/**
 * Offline Authentication Service
 * Ermöglicht Login ohne Internet-Verbindung
 * Speichert alle Business-Daten lokal (KV / StorageEngine)
 */

import { kvGet, kvSet, kvRemove, kvSetPersisted } from '../infrastructure/storage/kvAccess';
import {
  establishOfflineBusinessSession
} from './offlineAuthSession';
import {
  credentialsMatch,
  ensureAuthStorageRehydrated,
  extractBusinessesFromOfflineUsers,
  findBusinessForLogin,
  mergeOfflineBusinessRecords,
  parseOfflineBusinessList,
  readAuthListFromNativeLocalStorage,
  readOfflineBusinesses,
  readOfflineBusinessesMerged,
  readOfflineBusinessesMergedSync,
  resolveBusinessPasswordHash,
  triggerReinstallRecoveryIfNeeded,
  writeAuthListToNativeLocalStorage
} from '../utils/authCredentialRead';
import { buildOfflineLoginDebug, logOfflineLoginDebug } from '../utils/authLoginDebug';
import {
  hashPasswordPbkdf2,
  isPbkdf2PasswordHash,
  verifyPasswordPbkdf2
} from '../utils/passwordKdf';
import { normalizeBusinessStorageId } from '../utils/sharedCore';

class OfflineAuthService {
  constructor() {
    this.businessesKey = 'offline_businesses';
    this.usersKey = 'offline_users';
  }

  _persistOfflineList(key, list) {
    writeAuthListToNativeLocalStorage(key, list);
    kvSet(key, list);
    void kvSetPersisted(key, list);
  }

  /**
   * Registriere ein neues Business offline
   */
  async registerBusiness(businessData, options = {}) {
    const { skipSession = false } = options;
    try {
      const { isRegistrationAllowedOnThisInstallation } = await import(
        '../utils/installationRegistrationPolicy'
      );
      if (!isRegistrationAllowedOnThisInstallation()) {
        return {
          success: false,
          errorCode: 'INSTALLATION_ALREADY_CONFIGURED',
          error:
            'Diese Installation ist bereits konfiguriert. Es kann kein weiteres Konto registriert werden.'
        };
      }

      // Generiere eindeutige ID
      const businessId = `business_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const business = {
        id: businessId,
        business_name: businessData.business_name,
        owner_name: businessData.owner_name,
        nif: businessData.nif,
        phone: businessData.phone,
        address: businessData.address,
        email: businessData.email,
        username: businessData.username,
        password: await this.hashPassword(String(businessData.password || '').trim()),
        // PBKDF2 — never store plaintext manager PIN
        manager_code: await this.hashPassword(String(businessData.manager_code || '').trim()),
        business_type: businessData.business_type,
        logo: businessData.logo?.preview || null,
        status: 'active',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        offline: true // Markiere als offline Business
      };

      // Lade bestehende Businesses
      const businesses = this.getBusinesses();
      businesses.push(business);
      
      kvSet(this.businessesKey, businesses);
      await kvSetPersisted(this.businessesKey, businesses);
      writeAuthListToNativeLocalStorage(this.businessesKey, businesses);
      
      // Erstelle auch User-Eintrag
      const user = {
        id: businessId,
        type: 'business',
        business: business,
        created_at: new Date().toISOString()
      };
      
      const users = this.getUsers();
      users.push(user);
      kvSet(this.usersKey, users);
      await kvSetPersisted(this.usersKey, users);
      writeAuthListToNativeLocalStorage(this.usersKey, users);

      let sessionUser = null;
      if (!skipSession) {
        ({ user: sessionUser } = await establishOfflineBusinessSession(business, {
          awaitBootstrap: true,
          dispatchAuthEvent: false
        }));
      }

      return {
        success: true,
        business: business,
        user: user,
        sessionUser
      };
    } catch (error) {
      console.error('Error registering business offline:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Login offline (aus lokalem KV)
   */
  async login(username, password) {
    try {
      const trimmedPassword = String(password || '').trim();
      // FIX: On reinstall KV + localStorage are empty. Trigger AppData restore BEFORE
      // the sync fast-path so credentials are available on the first login attempt.
      await triggerReinstallRecoveryIfNeeded();
      // Fast path: sync KV/localStorage first — avoid IDB rehydrate on every click
      let businesses = readOfflineBusinessesMergedSync();
      let business = findBusinessForLogin(username, businesses);
      if (!business) {
        await ensureAuthStorageRehydrated();
        businesses = await readOfflineBusinessesMerged({ repair: false, rehydrate: false });
        business = findBusinessForLogin(username, businesses);
      } else {
        // Repair/rehydrate in background — must not block password verify
        void ensureAuthStorageRehydrated()
          .then(() => readOfflineBusinessesMerged({ repair: true, rehydrate: false }))
          .catch(() => {});
      }

      const businessesFromUsers = extractBusinessesFromOfflineUsers([
        ...readAuthListFromNativeLocalStorage('offline_users'),
        ...parseOfflineBusinessList(kvGet('offline_users'))
      ]);

      if (business) {
        const passwordHash = resolveBusinessPasswordHash(business, businessesFromUsers);
        if (passwordHash && !business.password) {
          business = { ...business, password: passwordHash };
          this.updateBusiness(business.id, { password: passwordHash });
        }
      }

      if (!business) {
        const failResult = {
          success: false,
          error: 'Invalid username or password',
          errorCode: 'ACCOUNT_NOT_FOUND'
        };
        const debug = buildOfflineLoginDebug(username, failResult);
        logOfflineLoginDebug(debug);
        return { ...failResult, debug };
      }

      const storedPassword =
        resolveBusinessPasswordHash(business, businessesFromUsers) || business.password;
      const passwordOk = await this.verifyPassword(trimmedPassword, storedPassword);
      if (!passwordOk) {
        const diagnostic = {
          hasStoredPassword: storedPassword != null && String(storedPassword).length > 0,
          businessId: business.id,
          username: business.username,
          email: business.email,
          passwordFromOfflineUsers:
            !!(storedPassword && !business.password)
        };
        const failResult = {
          success: false,
          error: 'Invalid username or password',
          errorCode: diagnostic.hasStoredPassword ? 'WRONG_PASSWORD' : 'PASSWORD_MISSING',
          diagnostic
        };
        const debug = buildOfflineLoginDebug(username, failResult);
        logOfflineLoginDebug(debug);
        return { ...failResult, debug };
      }

      business = { ...business, password: storedPassword };
      // Legacy hash upgrade after unlock — second PBKDF2 must not delay session
      if (!isPbkdf2PasswordHash(storedPassword)) {
        void this.hashPassword(trimmedPassword)
          .then((upgradedHash) => {
            this.updateBusiness(business.id, { password: upgradedHash });
          })
          .catch(() => {});
      }

      // Lade User
      const users = this.getUsers();
      let user = users.find(u => u.id === business.id);
      
      if (!user) {
        user = {
          id: business.id,
          type: 'business',
          business: business,
          created_at: new Date().toISOString()
        };
        users.push(user);
        this._persistOfflineList(this.usersKey, users);
      } else {
        // Update business data in user
        user.business = business;
      }

      return {
        success: true,
        business: business,
        user: user
      };
    } catch (error) {
      console.error('Error logging in offline:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  _parseOfflineList(data) {
    return parseOfflineBusinessList(data);
  }

  _readOfflineListFromLocalStorage(key) {
    return readAuthListFromNativeLocalStorage(key);
  }

  _mergeBusinessSources() {
    const users = this._readOfflineList(this.usersKey);
    const userBusinesses = users
      .map((entry) => (entry && typeof entry === 'object' ? entry.business : null))
      .filter(Boolean);
    const currentUser = kvGet('currentUser');
    const sessionBusiness =
      currentUser?.business?.id && typeof currentUser.business === 'object'
        ? [currentUser.business]
        : [];

    return mergeOfflineBusinessRecords(
      this._readOfflineListFromLocalStorage(this.businessesKey),
      this._readOfflineList(this.businessesKey),
      userBusinesses,
      sessionBusiness
    );
  }

  _mergeOfflineUserLists(fromLs, fromKv) {
    const businesses = mergeOfflineBusinessRecords(
      fromLs.map((u) => u?.business).filter(Boolean),
      fromKv.map((u) => u?.business).filter(Boolean)
    );
    const byId = new Map([...fromLs, ...fromKv].map((u) => [String(u?.id), u]));
    return businesses.map((business) => {
      const prev = byId.get(String(business.id));
      return {
        id: business.id,
        type: 'business',
        business,
        created_at: prev?.created_at || business.created_at || new Date().toISOString()
      };
    });
  }

  _syncRepairedAuthList(key, merged, fromKv) {
    const needsRepair = merged.some((entry) => {
      const prev = fromKv.find((c) => String(c?.id) === String(entry?.id));
      const passwordField = key === this.usersKey ? entry?.business?.password : entry?.password;
      const prevPassword = key === this.usersKey ? prev?.business?.password : prev?.password;
      const codeField = key === this.usersKey ? entry?.business?.manager_code : entry?.manager_code;
      const prevCode = key === this.usersKey ? prev?.business?.manager_code : prev?.manager_code;
      return (passwordField && !prevPassword) || (codeField && !prevCode);
    });
    if (!needsRepair) return;
    writeAuthListToNativeLocalStorage(key, merged);
    kvSet(key, merged);
    void kvSetPersisted(key, merged);
  }

  /**
   * Liest offline_* — localStorage (AppData) zuerst, dann KV/IndexedDB.
   */
  _readOfflineList(key) {
    try {
      const fromLs = this._parseOfflineList(
        typeof window !== 'undefined' && window.localStorage
          ? window.localStorage.getItem(key)
          : null
      );
      const fromKv = this._parseOfflineList(kvGet(key));

      if (key === this.usersKey) {
        const merged = this._mergeOfflineUserLists(fromLs, fromKv);
        if (merged.length > 0) {
          this._syncRepairedAuthList(key, merged, fromKv);
          return merged;
        }
        return fromKv;
      }

      const merged = mergeOfflineBusinessRecords(fromLs, fromKv);
      if (merged.length > 0) {
        this._syncRepairedAuthList(key, merged, fromKv);
        return merged;
      }
      return fromKv;
    } catch (error) {
      console.error(`Error reading ${key}:`, error);
      return readAuthListFromNativeLocalStorage(key);
    }
  }

  /**
   * Hole alle Businesses
   */
  getBusinesses() {
    return this._mergeBusinessSources();
  }

  /**
   * Hole alle Users
   */
  getUsers() {
    return this._readOfflineList(this.usersKey);
  }

  /**
   * Passwort-Hashing (PBKDF2 via Web Crypto).
   */
  async hashPassword(password) {
    return hashPasswordPbkdf2(password);
  }

  /** Legacy 32-bit Hash — nur zur Verifikation alter Einträge. */
  _hashPasswordLegacyStable(password) {
    const input = String(password ?? '');
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      hash = ((hash << 5) - hash) + input.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash).toString(36);
  }

  /** Legacy-Hash (vor Stabilisierung, ohne trim / ohne |0). */
  _hashPasswordLegacy(password) {
    const input = String(password ?? '');
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }

  /**
   * @param {string} entered
   * @param {string|null|undefined} storedPassword
   */
  async verifyPassword(entered, storedPassword) {
    if (storedPassword == null || storedPassword === '') return false;

    const trimmed = String(entered ?? '').trim();
    const raw = String(entered ?? '');
    const candidates = [trimmed];
    if (raw !== trimmed) candidates.push(raw);

    const stored = String(storedPassword);

    if (isPbkdf2PasswordHash(stored)) {
      for (const candidate of candidates) {
        if (await verifyPasswordPbkdf2(candidate, stored)) return true;
      }
      return false;
    }

    for (const candidate of candidates) {
      if (stored === this._hashPasswordLegacyStable(candidate)) return true;
      if (stored === this._hashPasswordLegacy(candidate)) return true;
      // Plaintext-Migration (alte Daten) — kein neuer Plaintext-Speicher
      if (stored === candidate) return true;
    }

    return false;
  }

  /**
   * Entfernt ein frisch registriertes Business (Rollback bei fehlgeschlagener Gerätebindung).
   */
  removeRegisteredBusiness(businessId) {
    if (!businessId) {
      return { success: false, error: 'No business ID' };
    }

    try {
      const businesses = this.getBusinesses().filter((b) => b.id !== businessId);
      this._persistOfflineList(this.businessesKey, businesses);

      const users = this.getUsers().filter((u) => u.id !== businessId);
      this._persistOfflineList(this.usersKey, users);

      const currentUser = kvGet('currentUser');
      let sessionBusinessId = null;
      if (currentUser && typeof currentUser === 'object') {
        sessionBusinessId =
          currentUser.business?.id || currentUser.user?.id || currentUser.id || null;
      } else if (typeof currentUser === 'string' && currentUser.trim()) {
        try {
          const parsed = JSON.parse(currentUser);
          sessionBusinessId = parsed?.business?.id || parsed?.user?.id || parsed?.id || null;
        } catch {
          /* ignore */
        }
      }

      if (sessionBusinessId === businessId) {
        kvRemove('currentUser');
        kvRemove('currentBusiness');
        kvRemove('managerCode');
      }

      kvRemove(`business_${businessId}_machine_id`);
      kvRemove(`business_${businessId}_machine_id_saved_at`);

      return { success: true };
    } catch (error) {
      console.error('Error removing registered business:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Business anhand Benutzername oder E-Mail (offline, localStorage-first).
   * @param {string} identifier
   */
  async findBusinessByIdentifier(identifier) {
    await ensureAuthStorageRehydrated();
    const businesses = await readOfflineBusinessesMerged({ repair: true, rehydrate: false });
    return findBusinessForLogin(identifier, businesses);
  }

  /**
   * Offline Passwort zurücksetzen — Verifikation per Manager-Code oder NIF.
   * @param {string} identifier — username oder email
   * @param {string} verificationCode — manager_code oder nif
   * @param {string} newPassword
   */
  async resetLocalPassword(identifier, verificationCode, newPassword) {
    try {
      await ensureAuthStorageRehydrated();
      const business = await this.findBusinessByIdentifier(identifier);
      if (!business) {
        return { success: false, errorCode: 'ACCOUNT_NOT_FOUND' };
      }

      const code = String(verificationCode || '').trim();
      const { verifyStoredSecret } = await import('../utils/authCredentialRead');
      const managerOk = await verifyStoredSecret(code, business.manager_code);
      const nifOk = credentialsMatch(business.nif, code);
      if (!managerOk && !nifOk) {
        return { success: false, errorCode: 'INVALID_VERIFICATION_CODE', businessId: business.id };
      }

      const trimmed = String(newPassword || '').trim();
      if (trimmed.length < 8 || !/[A-Za-z]/.test(trimmed) || !/\d/.test(trimmed)) {
        return { success: false, errorCode: 'PASSWORD_TOO_SHORT' };
      }

      const result = this.updateBusiness(business.id, {
        password: await this.hashPassword(trimmed),
        resetToken: null,
        resetTokenExpiry: null
      });

      if (!result.success) {
        return { success: false, errorCode: 'UPDATE_FAILED', error: result.error };
      }

      console.info('[offlineAuth] Local password reset OK:', business.id);
      return { success: true, business: result.business };
    } catch (error) {
      console.error('[offlineAuth] resetLocalPassword failed:', error);
      return { success: false, errorCode: 'UPDATE_FAILED', error: error.message };
    }
  }

  /**
   * Cloud-Login (Netlify): lokales offline_businesses anlegen/aktualisieren,
   * damit derselbe Browser auch offline/local einloggen kann.
   * @param {object} cloudBusiness — Zeile aus Supabase businesses
   * @param {string} plainPassword
   */
  async upsertBusinessFromCloud(cloudBusiness, plainPassword) {
    try {
      if (!cloudBusiness?.id) {
        return { success: false, error: 'invalid_cloud_business' };
      }

      const storageId =
        normalizeBusinessStorageId(cloudBusiness.id) || String(cloudBusiness.id);
      const cloudId = String(cloudBusiness.id);
      const offlineId = cloudId.startsWith('business_') ? cloudId : `business_${storageId}`;
      const passwordHash = await this.hashPassword(String(plainPassword || '').trim());

      const businesses = this.getBusinesses();
      const index = businesses.findIndex(
        (b) =>
          normalizeBusinessStorageId(b?.id) === storageId ||
          String(b?.cloudId) === String(cloudBusiness.id)
      );

      const prevLocal = index >= 0 ? businesses[index] : null;
      // Prefer cloud hash; keep local if cloud cleared legacy plaintext without hash.
      const resolvedManagerCode =
        cloudBusiness.manager_code || prevLocal?.manager_code || null;

      const base = {
        business_name: cloudBusiness.business_name,
        owner_name: cloudBusiness.owner_name,
        nif: cloudBusiness.nif,
        phone: cloudBusiness.phone,
        address: cloudBusiness.address,
        email: cloudBusiness.email,
        username: cloudBusiness.username,
        password: passwordHash,
        manager_code: resolvedManagerCode,
        business_type: cloudBusiness.business_type,
        logo: cloudBusiness.logo || null,
        status: 'active',
        cloudId: cloudBusiness.id,
        cloud_plan: cloudBusiness.cloud_plan || 'none',
        subscription_type: cloudBusiness.subscription_type || null,
        subscription_amount: cloudBusiness.subscription_amount ?? null,
        subscription_expiry_date: cloudBusiness.subscription_expiry_date || null,
        supabaseAuth: true,
        offline: false,
        updated_at: new Date().toISOString()
      };

      let business;
      if (index >= 0) {
        business = {
          ...businesses[index],
          ...base,
          id: businesses[index].id || offlineId
        };
        businesses[index] = business;
      } else {
        business = {
          id: offlineId,
          ...base,
          created_at: new Date().toISOString()
        };
        businesses.push(business);
      }

      this._persistOfflineList(this.businessesKey, businesses);

      const users = this.getUsers();
      const userIndex = users.findIndex(
        (u) =>
          normalizeBusinessStorageId(u?.id) === storageId ||
          normalizeBusinessStorageId(u?.business?.id) === storageId
      );
      const user = {
        id: business.id,
        type: 'business',
        business,
        created_at: users[userIndex]?.created_at || new Date().toISOString()
      };
      if (userIndex >= 0) {
        users[userIndex] = user;
      } else {
        users.push(user);
      }
      this._persistOfflineList(this.usersKey, users);

      try {
        const { markInstallationConfigured } = await import(
          '../utils/installationRegistrationPolicy'
        );
        markInstallationConfigured();
      } catch {
        /* non-blocking */
      }

      return { success: true, business };
    } catch (error) {
      console.error('Error upserting cloud business locally:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update Business-Daten
   */
  updateBusiness(businessId, updates) {
    try {
      const businesses = this.getBusinesses();
      const index = businesses.findIndex(b => b.id === businessId);
      
      if (index === -1) {
        return { success: false, error: 'Business not found' };
      }

      businesses[index] = {
        ...businesses[index],
        ...updates,
        updated_at: new Date().toISOString()
      };

      this._persistOfflineList(this.businessesKey, businesses);
      
      // Update auch in Users
      const users = this.getUsers();
      const userIndex = users.findIndex(u => u.id === businessId);
      if (userIndex !== -1) {
        users[userIndex].business = businesses[index];
        this._persistOfflineList(this.usersKey, users);
      }

      return {
        success: true,
        business: businesses[index]
      };
    } catch (error) {
      console.error('Error updating business:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export const offlineAuthService = new OfflineAuthService();
export default offlineAuthService;
